import { useState } from "react";
import axios from "axios";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { toast } from "sonner";
import { Send, Mail, Github, Linkedin, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", brief: "" });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.brief || form.brief.trim().length < 6) {
      toast.error("MISSING FIELDS // EMAIL AND BRIEF (6+ CHARS) REQUIRED");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name || null,
        email: form.email,
        brief: form.brief,
      });
      toast.success("TRANSMISSION QUEUED // I'LL BE IN TOUCH.");
      setForm({ name: "", email: "", brief: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "TRANSMISSION FAILED // TRY AGAIN";
      toast.error(typeof detail === "string" ? detail : "TRANSMISSION FAILED // TRY AGAIN");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel id="SYS_08">CONTACT // OPEN_CHANNEL</SectionLabel>

        <Reveal>
          <h2 className="font-['Unbounded'] font-black text-[2.25rem] sm:text-[3rem] md:text-[4rem] lg:text-[5rem] xl:text-[6rem] leading-[0.95] sm:leading-[0.9] tracking-tighter text-[#F5F5F5]">
            SYSTEM_HALT
            <br />
            <span className="text-[#00FF66]">// LET'S TALK.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66] mb-4">
              &gt;_ DIRECT_CHANNELS
            </div>
            <ul className="space-y-4 font-['JetBrains_Mono'] text-sm">
              <li className="flex items-center gap-3">
                <Mail size={14} strokeWidth={1.5} className="text-[#00FF66]" />
                <a
                  data-testid="contact-email-link"
                  href="mailto:iamrakpan@gmail.com"
                  className="text-[#F5F5F5] link-underline"
                >
                  iamrakpan@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Linkedin size={14} strokeWidth={1.5} className="text-[#00FF66]" />
                <a
                  data-testid="contact-linkedin"
                  href="https://linkedin.com/in/richardakpan"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#F5F5F5] link-underline"
                >
                  linkedin.com/in/richardakpan
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Github size={14} strokeWidth={1.5} className="text-[#00FF66]" />
                <a
                  data-testid="contact-github"
                  href="https://github.com/ra-sys-io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#F5F5F5] link-underline"
                >
                  github.com/ra-sys-io
                </a>
              </li>
              <li>
                <a
                  data-testid="contact-resume"
                  href="/resume/richard-akpan.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 border border-[#00FF66]/40 text-[#00FF66] px-4 py-2 hover:bg-[#00FF66] hover:text-black font-['JetBrains_Mono'] text-[11px] tracking-[0.3em]"
                  style={{ transition: "background-color 0.15s, color 0.15s" }}
                >
                  <FileText size={12} strokeWidth={1.5} />
                  DOWNLOAD RESUME
                </a>
              </li>
            </ul>

            <div className="mt-10 border border-white/10 p-5 bg-[#0A0A0A]">
              <div className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#A1A1AA]">
                TYPICAL_RESPONSE
              </div>
              <div className="mt-2 font-['Unbounded'] font-bold text-xl text-[#F5F5F5]">
                &lt; 48 HOURS
              </div>
              <div className="mt-2 font-['JetBrains_Mono'] text-[11px] text-[#A1A1AA]">
                Faster for recruiters, hiring managers, and collab requests.
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.2}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-white/10 bg-[#0A0A0A] p-6 lg:p-8 font-['JetBrains_Mono']"
            >
              <div className="mb-8">
                <div className="text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-2">
                  <span className="text-[#00FF66]">root@server:~#</span> IDENT_NAME
                </div>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={handle("name")}
                  placeholder="your name (optional)"
                  className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#00FF66] outline-none py-2 text-[#F5F5F5] placeholder:text-white/25 focus:placeholder:text-transparent"
                  style={{ transition: "border-color 0.2s" }}
                />
              </div>

              <div className="mb-8">
                <div className="text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-2">
                  <span className="text-[#00FF66]">root@server:~#</span> RETURN_CHANNEL
                </div>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handle("email")}
                  placeholder="you@company.com"
                  className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#00FF66] outline-none py-2 text-[#F5F5F5] placeholder:text-white/25 focus:placeholder:text-transparent"
                  style={{ transition: "border-color 0.2s" }}
                />
              </div>

              <div className="mb-10">
                <div className="text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-2">
                  <span className="text-[#00FF66]">root@server:~#</span> BRIEF
                </div>
                <textarea
                  data-testid="contact-input-brief"
                  required
                  rows={4}
                  value={form.brief}
                  onChange={handle("brief")}
                  placeholder="scope, timeline, and how sensitive is this line..."
                  className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#00FF66] outline-none py-2 text-[#F5F5F5] placeholder:text-white/25 focus:placeholder:text-transparent resize-none"
                  style={{ transition: "border-color 0.2s" }}
                />
              </div>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-3 border border-[#00FF66] text-[#00FF66] px-6 py-3 text-[11px] tracking-[0.3em] hover:bg-[#00FF66] hover:text-black disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ transition: "background-color 0.15s, color 0.15s" }}
              >
                {submitting ? "TRANSMITTING..." : "TRANSMIT"}
                <Send size={12} strokeWidth={1.5} />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
